import { createUseStyles } from 'react-jss';
import { CustomCiamUiTheme, linkColor } from '../../../theme';

const focusedWithWhiteBorder = {
  '&:before, &:before': {
    margin: 1,
    borderRadius: 3,
    borderColor: '#fff',
    opacity: 0,
  },

  '&:focus-visible:before, $focus&:before': {
    margin: 1,
    borderRadius: 3,
    opacity: 1,
  },
};

const focusedWithBorder = {
  '&:focus-visible:before, $focus&:before': {
    borderColor: '#51B7EA',
  },
};

const disabledWithBackground = {
  '$disabled&': {
    pointerEvents: 'none',
    backgroundColor: 'rgba(0,0,0,.05) !important',
  },
};

const disabledWithoutBackground = {
  '$disabled&': {
    pointerEvents: 'none',
    backgroundColor: 'transparent',
  },
  '$disabled&:before': {
    opacity: 0,
  },
};

const ghostish = {
  '&:hover, $hover&': { backgroundColor: 'rgba(0,0,0,.08)' },
  '&:active, $active&': { backgroundColor: 'rgba(0,0,0,.12)' },
  ...disabledWithoutBackground,
  ...focusedWithBorder,
};

export const useStyles = createUseStyles(
  (theme: CustomCiamUiTheme) => ({
    root: {
      outline: 'none',
      fontSize: '1rem',
      userSelect: 'none',
      background: 'none',
      lineHeight: 20 / 14,
      border: 'none',
      position: 'relative',
      letterSpacing: 0,
      borderRadius: 8,
      padding: [4, 16],
      margin: 0,
      fontFamily: 'inherit',
      display: 'inline-block',
      transition: '.25s background-color',

      '&:before': {
        content: '""',
        border: '1px solid transparent',
        borderRadius: 4,
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        pointerEvents: 'none',
        transition: '.25s opacity, .25s border-color',
      },
    },

    loading: {
      pointerEvents: 'none',
      opacity: 0.5,
    },

    fullWidth: {
      width: '100%',
    },

    iconOnly: {},

    internal: {
      cursor: 'pointer',
      display: 'flex',

      '$medium &': {
        padding: '12px 16px',
      },

      '$small &': {
        padding: '8px 16px',
      },

      '$medium$iconOnly &': {
        padding: 12,
      },

      '$small$iconOnly &': {
        padding: 8,
      },
    },

    hover: {},
    active: {},
    focus: {},

    icon: {
      height: 20,
      width: 20,
      minHeight: `${20 / 14}em`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',

      '& + $label': {
        marginLeft: 8,
      },
    },

    /* SIZES */

    small: {},
    medium: {},

    ///

    label: {
      flexGrow: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'center',
    },

    disabled: {
      pointerEvents: 'none',
      '& $internal': {
        color: 'rgba(0,0,0,.12)',
      },

      '$root&:before': {
        opacity: 0,
      },

      '$root&:focus-visible:before': {
        opacity: 0,
      },
    },

    /* VARIANTS */
    primary: {
      color: '#fff',
      backgroundColor: theme.buttonColorPrimary,

      '&:hover, $hover&': {
        backgroundColor: '#0277B5',
      },

      '&:active, $active&': {
        backgroundColor: '#015990',
      },

      ...focusedWithWhiteBorder,
      ...disabledWithBackground,
    },

    secondary: {
      color: 'rgba(0,0,0,.94)',
      '&:before': {
        borderColor: 'rgba(0,0,0,.44)',
      },
      '$disabled&:before, $disabled&:focus-visible:before': {
        opacity: 1,
        borderColor: 'rgba(0,0,0,.08)',
      },
      ...focusedWithBorder,
    },

    ghost: {
      ...ghostish,
    },

    link: {
      color: linkColor,
      ...ghostish,
    },

    danger: {
      color: '#fff',
      backgroundColor: '#CF2E38',

      '&:hover, $hover&': {
        backgroundColor: '#A21B23',
      },

      '&:active, $active&': {
        backgroundColor: '#760E13',
      },

      ...disabledWithBackground,
      ...focusedWithWhiteBorder,
    },
  }),
  {
    name: 'Button',
  },
);
